/**
 * NotificationItem Component
 *
 * Individual notification card with action support.
 */

import { useNavigate } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import { X, ExternalLink } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNotificationContext } from '@/context/NotificationContext';
import { cn } from '@/lib/utils';
import type { Notification } from '@/types/notifications';
import { PRIORITY_CONFIG, CATEGORY_CONFIG } from '@/types/notifications';

interface NotificationItemProps {
  notification: Notification;
}

export function NotificationItem({ notification }: NotificationItemProps) {
  const navigate = useNavigate();
  const { markAsRead, dismiss, setIsOpen } = useNotificationContext();

  const priorityConfig = PRIORITY_CONFIG[notification.priority];
  const categoryConfig = CATEGORY_CONFIG[notification.category];

  // Get the category icon component
  const CategoryIcon = (LucideIcons as Record<string, React.ComponentType<{ className?: string }>>)[categoryConfig.icon] || LucideIcons.Bell;

  const isUnread = !notification.read_at;

  const handleClick = async () => {
    // Mark as read
    if (isUnread) {
      await markAsRead(notification.id);
    }

    // Navigate if action URL provided
    if (notification.action_url) {
      setIsOpen(false);
      navigate(notification.action_url);
    }
  };

  const handleDismiss = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await dismiss(notification.id);
  };

  return (
    <div
      className={cn(
        'group relative border rounded-md px-2 py-1.5 cursor-pointer transition-all',
        'hover:shadow-sm',
        priorityConfig.bgColor,
        priorityConfig.borderColor,
        isUnread && 'ring-1 ring-primary/20'
      )}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Dismiss button */}
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'absolute top-0.5 right-0.5 h-5 w-5',
          'opacity-0 group-hover:opacity-100 transition-opacity',
          'hover:bg-background/50'
        )}
        onClick={handleDismiss}
        aria-label="Dismiss notification"
      >
        <X className="h-2.5 w-2.5" />
      </Button>

      {/* Content */}
      <div className="pr-5">
        {/* Header row: category + timestamp */}
        <div className="flex items-center justify-between gap-1 mb-0.5">
          <div className="flex items-center gap-1">
            <CategoryIcon className={cn('h-2.5 w-2.5', categoryConfig.color)} />
            <span className="text-[9px] uppercase tracking-wide text-muted-foreground font-medium">
              {categoryConfig.label}
            </span>
            {isUnread && (
              <span className="h-1 w-1 rounded-full bg-primary" />
            )}
          </div>
          <span className="text-[9px] text-muted-foreground/60">
            {formatDistanceToNowStrict(new Date(notification.created_at), { addSuffix: true })}
          </span>
        </div>

        {/* Title */}
        <h4 className={cn(
          'font-medium text-xs leading-tight',
          priorityConfig.color
        )}>
          {notification.title}
        </h4>

        {/* Message - single line */}
        {notification.message && (
          <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug line-clamp-1">
            {notification.message}
          </p>
        )}

        {/* Action link - inline */}
        {notification.action_url && notification.action_label && (
          <button
            className="text-[10px] text-primary hover:underline mt-0.5 inline-flex items-center gap-0.5"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            {notification.action_label}
            <ExternalLink className="h-2.5 w-2.5" />
          </button>
        )}
      </div>
    </div>
  );
}
